import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ScrollWipeContext } from "./scrollwipeContext";

const clamp01 = (n) => Math.max(0, Math.min(1, n));
let _id = 0;

function splitWordsPreserveWhitespace(str) {
  return str.match(/\n|[^\S\n]+|\S+/g) || [];
}

export default function ScrollWipe({
  as: Tag = "p",
  className = "",
  children,
  mode = "char",
  softness = 0.03,
  baseOpacity = 0.06,
  start = 0.85,
  end = 0.25,
}) {
  const ref = useRef(null);
  const group = useContext(ScrollWipeContext);

  const idRef = useRef(null);
  if (idRef.current == null) idRef.current = `sw_${_id++}`;
  const id = idRef.current;

  const [tLocal, setTLocal] = useState(0);

  const wordTokens = useMemo(() => {
    if (typeof children !== "string") return null;
    if (mode === "block") return null;
    return splitWordsPreserveWhitespace(children);
  }, [children, mode]);

  // ← group removed from deps
  useEffect(() => {
    if (!group) return;
    const charLen = typeof children === "string" ? Array.from(children).length : 0;
    group.register(id, charLen);
    return () => group.unregister(id);
  }, [id, children]); // ← no group

  // fallback scroll tracking when not in a group
  useEffect(() => {
    if (group) return;
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const p = (start * vh - r.top) / ((start - end) * vh);
      setTLocal(clamp01(p));
    };
    const onScroll = () => { if (raf) return; raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [group, start, end]);

  // ← read from tRef, not group.t
  const t = group ? (group.tRef?.current ?? 0) : tLocal;

  const rendered = useMemo(() => {
    if (typeof children !== "string" || mode === "block") return children;

    const totalChars = Math.max(1, group?.total ?? Array.from(children).length);
    const offsetChars = group?.offsets?.get(id) ?? 0;
    let charCursor = 0;

    return (wordTokens || []).map((tok, wi) => {
      if (tok === "\n") { charCursor += 1; return <br key={`br-${wi}`} />; }
      if (/^[^\S\n]+$/.test(tok)) {
        charCursor += tok.length;
        return <span key={`sp-${wi}`} style={{ whiteSpace: "pre" }}>{tok}</span>;
      }
      const chars = Array.from(tok);
      const wordSpans = chars.map((ch, ci) => {
        const globalIndex = group ? offsetChars + charCursor : charCursor;
        const u = globalIndex / Math.max(1, totalChars - 1);
        const local = clamp01((t - u) / Math.max(1e-6, softness) + 1);
        const op = baseOpacity + (1 - baseOpacity) * local;
        charCursor += 1;
        return (
          <span key={`c-${wi}-${ci}`} style={{ opacity: op, display: "inline", transition: "opacity 60ms linear" }} aria-hidden="true">
            {ch}
          </span>
        );
      });
      return (
        <span key={`w-${wi}`} style={{ display: "inline-block", whiteSpace: "nowrap" }}>
          {wordSpans}
        </span>
      );
    });
  }, [children, mode, wordTokens, group, id, t, softness, baseOpacity]);

  return (
    <Tag ref={ref} className={className}>
      {rendered}
    </Tag>
  );
}
